import axios from "axios";

import type {
	BrainItem,
	CompanyProvider,
	ConnectedAccount,
	DriveItem,
	Integration,
	IntegrationName,
	IntegrationProvider,
	IntegrationScopeTypes,
	IntegrationWithCount,
} from "@virtual-brain-lib/types";

import env from "../environments";
import { infiniteQueryOptions, queryOptions } from "../query-client";

export async function initIntegration(
	provider: IntegrationProvider,
	integrationName: IntegrationName,
) {
	const response = await axios.post<{ url: string }>(
		`${env.apiUrl}/integrations/${provider}/init`,
		{ integrationName },
	);

	return response.data;
}

export const getBrainIntegrations = queryOptions({
	async queryFn({ queryKey }) {
		const [, { brainId }] = queryKey;

		const response = await axios.get<Integration[]>(
			`${env.apiUrl}/integrations/brain/${brainId}`,
		);

		return response.data;
	},
	queryKey: "brainIntegrations",
});

export const getConnectedAccounts = queryOptions({
	async queryFn() {
		const response = await axios.get<ConnectedAccount[]>(`${env.apiUrl}/integrations/accounts`);

		return response.data;
	},
	queryKey: "connectedAccounts",
});

export async function getCompanyIntegrations(companyId: string) {
	const response = await axios.get<CompanyProvider[]>(
		`${env.apiUrl}/integrations/company/${companyId}`,
	);

	return response.data;
}

export const getIntegration = queryOptions({
	async queryFn({ queryKey }) {
		const [, { integrationId }] = queryKey;
		const response = await axios.get<IntegrationWithCount>(
			`${env.apiUrl}/integrations/${integrationId}`,
		);

		return response.data;
	},
	queryKey: "integration",
});

export async function updateIntegration(integration: Partial<Integration> & { id: string }) {
	const response = await axios.put<Integration>(
		`${env.apiUrl}/integrations/${integration.id}`,
		integration,
	);

	return response.data;
}

export async function reparseFolder(
	integration: Partial<Integration> & { id: string },
	folderId: string,
) {
	await axios.post(`${env.apiUrl}/integrations/${integration.id}/reparse/${folderId}`);
}

export async function reparseIntegration(integration: Partial<Integration> & { id: string }) {
	await axios.post(`${env.apiUrl}/integrations/${integration.id}/reparse`);
}

export async function getIntegratedItemsFn(integrationId: string) {
	const response = await axios.get<{
		brainItems: BrainItem[];
	}>(`${env.apiUrl}/integrations/${integrationId}/integrated`);

	return response.data.brainItems;
}

export async function getSharedWithMeFilesFn(integrationIds: string[]) {
	const response = await axios.post<{ brainItems: BrainItem[] }>(
		`${env.apiUrl}/integrations/browse/sharedWithMe`,
		{
			integrationIds,
		},
	);

	return response.data.brainItems;
}

export async function getDriveFilesFn({
	integrationId,
	...params
}: {
	integrationId: string;
	scopeType?: IntegrationScopeTypes;
	folder?: string | undefined;
	sort?: string | undefined;
	query?: string | undefined;
}) {
	const response = await axios.get<{
		brainItems: BrainItem[];
	}>(`${env.apiUrl}/integrations/${integrationId}/browse`, {
		params,
	});

	return response.data.brainItems;
}

export const getIntegrationFiles = queryOptions({
	async queryFn({ queryKey }) {
		const [, { integrationIds, ...params }] = queryKey;
		const response: { integratedItems: BrainItem[]; driveFiles: BrainItem[] } = {
			integratedItems: [],
			driveFiles: [],
		};

		if (integrationIds.length > 1) {
			response.integratedItems = (
				await Promise.all(integrationIds.map(async (id) => getIntegratedItemsFn(id)))
			).flat();
			response.driveFiles = await getSharedWithMeFilesFn(integrationIds);
		} else if (integrationIds.length === 1) {
			const integrationId = integrationIds[0];

			const [driveFiles, integratedItems] = await Promise.all([
				getDriveFilesFn({
					integrationId,
					...params,
				}),
				getIntegratedItemsFn(integrationId),
			]);

			response.integratedItems = integratedItems;
			response.driveFiles = driveFiles;
		}

		return [
			...response.integratedItems,
			...(response.integratedItems.length
				? [
						{
							id: "separator",
							name: "separator",
							source: "integration",
						} satisfies BrainItem,
					]
				: []),
			...response.driveFiles,
		];
	},
	queryKey: "integrationFiles",
});

export async function getDriveFilesFolder({
	integrationId,
	...params
}: {
	integrationId: string;
	scopeType: IntegrationScopeTypes;
	folder: BrainItem;
	sort?: string | undefined;
}) {
	const response = await axios.get<{
		brainItems: BrainItem[];
		countTotal: number;
	}>(`${env.apiUrl}/integrations/${integrationId}/browse/folder`, {
		params,
	});

	return response.data;
}

export const getFiles = infiniteQueryOptions({
	async queryFn({ queryKey, pageParam }) {
		const [, { brainId, ...params }] = queryKey;
		const response = await axios.get<{
			brainItems: BrainItem[];
			countTotal: number;
		}>(`${env.apiUrl}/brains/${brainId}/files`, {
			params: { ...params, offset: pageParam },
		});

		return response.data;
	},
	queryKey: "files",
	initialPageParam: 0,
	getNextPageParam(lastPage, allPages) {
		const loadedCount = allPages.reduce((total, page) => total + page.brainItems.length, 0);

		return loadedCount < lastPage.countTotal ? loadedCount : undefined;
	},
});

export async function retryFile({ brainId, fileId }: { brainId: string; fileId: string }) {
	await axios.post(`${env.apiUrl}/brains/${brainId}/retry`, { fileId });
}

export async function getProviderDrives(provider: IntegrationName) {
	const response = await axios.get<DriveItem[]>(`${env.apiUrl}/integrations/browse-provider`, {
		params: {
			provider,
			scopeType: "drives",
			ignorePolicies: true,
		},
	});

	return response.data;
}

export async function browseProviderFiles(
	provider: IntegrationName,
	scopeType?: IntegrationScopeTypes,
	folder?: string | undefined,
	driveId?: string | undefined,
) {
	const response = await axios.get<DriveItem[]>(`${env.apiUrl}/integrations/browse-provider`, {
		params: {
			provider,
			scopeType,
			folder: folder ? encodeURI(folder) : undefined,
			driveId: driveId ? encodeURI(driveId) : undefined,
		},
	});

	return response.data;
}

export async function searchDrives(
	scopeType: IntegrationScopeTypes,
	query: string,
	provider: IntegrationName,
) {
	const response = await axios.post<DriveItem[]>(
		`${env.apiUrl}/integrations/browse-provider/search`,
		{
			scopeType,
			query,
			provider,
		},
	);

	return response.data;
}

export async function deleteIntegration({ integrationId }: { integrationId: string }) {
	return axios.delete(`${env.apiUrl}/integrations/${integrationId}`);
}

export async function addIntegrationContents(params: {
	itemsToAdd: Array<BrainItem | DriveItem>;
	itemsToRemove: Array<BrainItem | DriveItem>;
	brainId: string;
	scopeType: IntegrationScopeTypes;
	integrationName: IntegrationName;
}) {
	const response = await axios.post<{ integrations: Integration[] }>(
		`${env.apiUrl}/integrations/create-contents`,
		{
			itemsToUnselect: [],
			...params,
		},
	);

	return response.data;
}
